import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardActions,
    Grid,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    useTheme,
    Tooltip,
    InputAdornment,
    Avatar,
    Skeleton,
    Chip,
    Paper,
    Stack,
    Snackbar
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    AccountBalance,
    PeopleAlt,
    Search,
    HolidayVillage,
    HowToVote,
    Person
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

interface MasterConstituency {
    id: number;
    name: string;
    villages_count: number;
    voting_booths_count: number;
    citizens_count: number;
    created_at: string;
}

interface MasterDataTotals {
    constituencies: number;
    villages: number;
    voting_booths: number;
    citizens: number;
}

const formatCount = (value?: number) => (value ?? 0).toLocaleString('en-IN');

const MasterConstituencies = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const theme = useTheme();

    // State
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState<MasterConstituency | null>(null);
    const [formData, setFormData] = useState({ name: '' });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [search, setSearch] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<MasterConstituency | null>(null);

    // Fetch Master Constituencies
    const { data: constituenciesData, isLoading } = useQuery({
        queryKey: ['master-constituencies', search],
        queryFn: async () => {
            const params: any = { per_page: 100 };
            if (search) params.search = search;
            const res = await api.get('/superadmin/master-constituencies', { params });
            return res.data;
        },
        placeholderData: keepPreviousData,
    });

    const constituencies = constituenciesData?.data?.data || constituenciesData?.data || [];
    // Overall master data totals from the API — not limited to the current page or search.
    const totals: MasterDataTotals | undefined = constituenciesData?.totals;

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Create Mutation
    const createMutation = useMutation({
        mutationFn: async (data: { name: string }) => api.post('/superadmin/master-constituencies', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['master-constituencies'] });
            handleClose();
            showSnackbar('Constituency created successfully', 'success');
        },
        onError: (err: any) => {
            if (err.response?.status === 422 && err.response?.data?.errors) {
                setFormErrors(err.response.data.errors);
            } else {
                showSnackbar(err.response?.data?.message || 'An error occurred', 'error');
            }
        }
    });

    // Update Mutation
    const updateMutation = useMutation({
        mutationFn: async (data: { id: number, name: string }) => api.put(`/superadmin/master-constituencies/${data.id}`, { name: data.name }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['master-constituencies'] });
            handleClose();
            showSnackbar('Constituency updated successfully', 'success');
        },
        onError: (err: any) => {
            showSnackbar(err.response?.data?.message || 'Failed to update constituency', 'error');
        }
    });

    // Delete Mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => api.delete(`/superadmin/master-constituencies/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['master-constituencies'] });
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            showSnackbar('Constituency deleted successfully', 'success');
        },
        onError: (err: any) => showSnackbar(err.response?.data?.message || 'Failed to delete constituency', 'error')
    });

    const handleOpen = (item?: MasterConstituency) => {
        if (item) {
            setEditingItem(item);
            setFormData({ name: item.name });
        } else {
            setEditingItem(null);
            setFormData({ name: '' });
        }
        setFormErrors({});
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({ name: '' });
    };

    const handleSubmit = () => {
        if (!formData.name) {
            setFormErrors({ name: 'Name is required' });
            return;
        }

        if (editingItem) {
            updateMutation.mutate({ id: editingItem.id, name: formData.name });
        } else {
            createMutation.mutate(formData);
        }
    };

    const handleDeleteClick = (item: MasterConstituency) => {
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (itemToDelete) {
            deleteMutation.mutate(itemToDelete.id);
        }
    };

    const totalConstituencies = totals?.constituencies ?? constituencies.length;

    const headerChipSx = {
        color: 'white',
        borderColor: 'rgba(255,255,255,0.3)',
        '& .MuiChip-icon': { color: 'rgba(255,255,255,0.7)' }
    };

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    color: 'white',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}
            >
                <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AccountBalance fontSize="large" sx={{ opacity: 0.9 }} />
                        Master Data Management
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, opacity: 0.8, maxWidth: 600 }}>
                        Manage master constituencies, villages, voting booths, and citizens for assignment to tenants.
                    </Typography>
                    <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mt: 3 }}>
                        <Chip
                            icon={<AccountBalance />}
                            label={`${formatCount(totalConstituencies)} Constituencies`}
                            variant="outlined"
                            sx={headerChipSx}
                        />
                        <Chip
                            icon={<HolidayVillage />}
                            label={`${formatCount(totals?.villages)} Villages`}
                            variant="outlined"
                            sx={headerChipSx}
                        />
                        <Chip
                            icon={<HowToVote />}
                            label={`${formatCount(totals?.voting_booths)} Voting Booths`}
                            variant="outlined"
                            sx={headerChipSx}
                        />
                        <Chip
                            icon={<PeopleAlt />}
                            label={`${formatCount(totals?.citizens)} Citizens`}
                            variant="outlined"
                            sx={headerChipSx}
                        />
                    </Stack>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => handleOpen()}
                    sx={{
                        px: 3,
                        py: 1.2,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        textTransform: 'none',
                        fontSize: '1rem',
                        bgcolor: 'white',
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                    }}
                >
                    Add Constituency
                </Button>
            </Paper>

            {/* Controls Section */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                    All Master Constituencies
                </Typography>
                <TextField
                    placeholder="Search by name..."
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 300 }, bgcolor: 'background.paper', borderRadius: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Grid container spacing={3}>
                {isLoading ? (
                    // Loading Skeletons
                    Array.from(new Array(4)).map((_, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                            <Card sx={{ height: 280, borderRadius: 3 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <Skeleton variant="circular" width={48} height={48} />
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width="80%" height={32} />
                                            <Skeleton variant="text" width="50%" />
                                        </Box>
                                    </Box>
                                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                ) : (constituencies as MasterConstituency[]).length > 0 ? (
                    (constituencies as MasterConstituency[]).map((item) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={item.id}>
                            <Card
                                elevation={2}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'visible',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                                        borderColor: theme.palette.primary.main
                                    }
                                }}
                                onClick={() => navigate(`/super-admin/master-constituencies/${item.id}`)}
                            >
                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: theme.palette.primary.main,
                                                width: 50,
                                                height: 50,
                                                fontSize: '1.5rem',
                                                fontWeight: 'bold',
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                                            }}
                                        >
                                            {item.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <IconButton size="small" sx={{ opacity: 0.6 }}>
                                                <AccountBalance fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 1.3, mb: 0.5 }}>
                                        {item.name}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                                        ID: #{item.id}
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Paper variant="outlined" sx={{ p: 1, bgcolor: theme.palette.background.default, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                                                <HolidayVillage fontSize="small" color="primary" />
                                                <Typography variant="caption" fontWeight="medium">Villages: {formatCount(item.villages_count)}</Typography>
                                            </Box>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 1, bgcolor: theme.palette.background.default, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                                                <HowToVote fontSize="small" color="secondary" />
                                                <Typography variant="caption" fontWeight="medium">Voting Booths: {formatCount(item.voting_booths_count)}</Typography>
                                            </Box>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 1, bgcolor: theme.palette.background.default, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                                                <Person fontSize="small" color="success" />
                                                <Typography variant="caption" fontWeight="medium">Citizens: {formatCount(item.citizens_count)}</Typography>
                                            </Box>
                                        </Paper>
                                    </Box>
                                </CardContent>
                                <CardActions sx={{
                                    px: 2,
                                    py: 1.5,
                                    justifyContent: 'space-between',
                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                                    borderTop: `1px solid ${theme.palette.divider}`
                                }}>
                                    <Button
                                        size="small"
                                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/super-admin/master-constituencies/${item.id}`);
                                        }}
                                    >
                                        View Details
                                    </Button>
                                    <Box>
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpen(item);
                                                }}
                                                sx={{
                                                    color: theme.palette.warning.main,
                                                    '&:hover': { bgcolor: theme.palette.warning.light + '20' }
                                                }}
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteClick(item);
                                                }}
                                                sx={{
                                                    color: theme.palette.error.main,
                                                    '&:hover': { bgcolor: theme.palette.error.light + '20' }
                                                }}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Grid size={{ xs: 12 }}>
                        <Paper
                            sx={{
                                p: 6,
                                textAlign: 'center',
                                borderRadius: 4,
                                border: '2px dashed',
                                borderColor: 'divider',
                                bgcolor: 'background.default'
                            }}
                        >
                            <Box sx={{ mb: 2, opacity: 0.5 }}>
                                <AccountBalance sx={{ fontSize: 60, color: 'text.secondary' }} />
                            </Box>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {search ? `No constituencies found matching "${search}"` : "No master constituencies yet"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                                Get started by adding your first master constituency to manage location and citizen data.
                            </Typography>
                            {!search && (
                                <Button
                                    variant="outlined"
                                    startIcon={<Add />}
                                    onClick={() => handleOpen()}
                                >
                                    Create New Constituency
                                </Button>
                            )}
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {/* Create/Edit Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3, p: 1 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingItem ? 'Edit Constituency' : 'Add New Constituency'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <TextField
                            autoFocus
                            label="Constituency Name"
                            placeholder="e.g. Hyderabad Central"
                            fullWidth
                            variant="outlined"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            error={!!formErrors.name}
                            helperText={formErrors.name}
                            disabled={createMutation.isPending || updateMutation.isPending}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><AccountBalance color="action" /></InputAdornment>,
                            }}
                            sx={{ mb: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose} sx={{ borderRadius: 2, textTransform: 'none' }} size="large">Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={createMutation.isPending || updateMutation.isPending}
                        sx={{ borderRadius: 2, px: 4, textTransform: 'none' }}
                        size="large"
                    >
                        {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save Constituency'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
                <Box sx={{ bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #fecaca' }}>
                    <Box sx={{ bgcolor: '#dc2626', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to delete <strong>"{itemToDelete?.name}"</strong>?
                    </Typography>
                    <Alert severity="warning" sx={{ bgcolor: '#fef3c7', border: '1px solid #fcd34d', '& .MuiAlert-icon': { color: '#d97706' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Warning: This will also delete all associated villages, voting booths, and citizens!
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}
                        startIcon={<Delete />} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar Messages */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MasterConstituencies;
